import { BookingEventModel } from '../../../data/entities/booking/BookingEventModel';
import { formatDateToLongUsWeekday, to12HourTime } from '../../../utils/dateUtils';
import { Card } from '../../../components';
import { formatPrice, getChargeLabel } from '../../../utils';
import { ServiceLocationService } from '../../../services';
import { useAsync } from '../../../hooks';

interface BookingEventSummaryProps {
  event: BookingEventModel;
  bookingDate: Date;
  locationId: number; 
}

const BookingEventSummary = ({ event, bookingDate, locationId } : BookingEventSummaryProps) => {
  const serviceId = event.serviceId;
  const serviceLocationService = new ServiceLocationService()
  const { value: servicePriceInfo, loading } = useAsync(
    () => {
      if(!serviceId || !locationId) return Promise.resolve(null); 
      return serviceLocationService.getEffectiveServicePriceAsync(serviceId, locationId)
    }, [serviceId, locationId]
  );

  if (loading || !servicePriceInfo) {
    return <Card className="mb-3 shadow-sm"><div className="p-4">Loading...</div></Card>;
  }

  const dueToday = servicePriceInfo.finalChargeAmount;
 
  const chargeLabel = getChargeLabel(servicePriceInfo.appliedChargeType);

  const mobileDate = bookingDate.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  return (
    <Card key={event.id} className="mb-3 shadow-sm">
      <div className="p-2">
        <div className="mb-3">
          <h5 className="fw-bold mb-0">{event.service?.name}</h5>
        </div>

        {(event.provider?.name || event.isAnyProvider) && (
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted">Provider</span>
            <span>{event.provider?.name ?? 'Any Provider'}</span>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="text-muted">Date</span>
          {/* Desktop: full date with weekday */}
          <span className="d-none d-md-inline">{formatDateToLongUsWeekday(bookingDate.toLocaleDateString())}</span>
          {/* Mobile: shorter date without weekday */}
          <span className="d-md-none">{mobileDate}</span>
        </div>

        {/* Desktop: time range */}
        <div className="d-none d-md-flex justify-content-between align-items-center mb-2">
          <span className="text-muted">Time</span>
          <span>
            {to12HourTime(event.startTime)}
            {/* <span className="mx-2">-</span>
            {to12HourTime(event.endTime)} */}
          </span>
        </div>

        <div className="d-none d-md-flex justify-content-between align-items-center mb-3">
          <span className="text-muted">Duration</span>
          <span>{event.service?.defaultDuration} min</span>
        </div>

        {/* Mobile */}
        <div className="d-md-none">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted">Time</span>
            <span>{to12HourTime(event.startTime)}</span>
          </div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="text-muted">Duration</span>
            <span>{event.service?.defaultDuration} min</span>
          </div>
        </div>

        <hr className="my-3" />

        {/* Pricing Section */}
        <div className="mt-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted">Service Price</span>
            <span>{formatPrice(servicePriceInfo.effectivePrice)}</span>
          </div>
          
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-primary small">{chargeLabel}</span>
            <span className="text-primary fw-semibold">{formatPrice(dueToday)}</span>
          </div>
        </div>        
      </div>
    </Card>
  );
};

export default BookingEventSummary;